/**
 * Route gate for MSI3 / AcuityDrill faculty tools:
 * - loading → Loading page
 * - unsigned → auth with next=
 * - not faculty (no grant) and not admin → home
 * - faculty grant OR global admin → children
 *
 * The faculty grant is server-authoritative; callables re-check it. This gate
 * is a UX guard only.
 */
import { Navigate, useLocation } from "react-router-dom";
import { Alert, Container } from "@mui/material";
import Loading from "components/Loading";
import { useAuth } from "hooks/useAuth";
import { useMsi3FacultyGrant } from "../msi3FacultyGrant";

export default function RequireMsi3Faculty({ children }) {
  const { user } = useAuth();
  const { loading, isFaculty, grantStatus } = useMsi3FacultyGrant();
  const location = useLocation();

  if (loading) return <Loading page />;

  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?next=${encodeURIComponent(next)}`} replace />;
  }

  if (!isFaculty && grantStatus === "pending") {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="info" data-testid="msi3-faculty-awaiting-approval">
          Awaiting platform approval.
        </Alert>
      </Container>
    );
  }

  // Signed in but not faculty and not admin → no access.
  if (!isFaculty) {
    return <Navigate to="/" replace />;
  }

  return children;
}
